import { useEffect, useState } from 'react'
import { useOnlineStatus } from '../hooks/useOnlineStatus'
import { useOrders } from '../context/OrdersContext'
import { useStuckRows } from '../hooks/useStuckRows'
import { StuckDialog } from './StuckDialog'
import { lastSynced, onSynced } from '../utils/localCache'
import './SyncBadge.css'

const clock = (at: number | null) =>
  at ? new Date(at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : null

/**
 * The operator has to be able to trust the box. This states plainly whether
 * anything is still unsynced, but selling is never blocked on it.
 *
 * Worded as what is happening to the orders, in the words the people using it
 * would use. "Çevrimdışı" is a fact about the wifi and leaves the real question
 * open, whether the sale just rung up is safe, and "sunucu" is not a word
 * anyone at a restaurant counter needs to learn.
 *
 * On a phone the sentence is dropped and only the dot is left: the top bar also
 * carries the screen title and the menu, and a badge long enough to say this
 * properly either pushed them off or was cut mid-word. Tapping brings the words
 * back, the colour is what gets glanced at, the sentence is what gets checked.
 */
export function SyncBadge() {
  const online = useOnlineStatus()
  const { pendingCount } = useOrders()
  /** Sunucuya gidemeyen kayıtlar: kırmızı, basınca listesi açılır. */
  const stuck = useStuckRows()
  const [showStuck, setShowStuck] = useState(false)
  /** Phone only: whether the tapped-open explanation is showing. */
  const [open, setOpen] = useState(false)
  /** Sunucudan en son taze veri ne zaman geldi (çevrimdışıyken yazılıyor). */
  const [syncedAt, setSyncedAt] = useState(lastSynced)
  useEffect(() => onSynced(setSyncedAt), [])
  const since = clock(syncedAt)

  // Closes itself. Nobody taps this twice, and a bubble left hanging over the
  // menu is worse than the question it answered.
  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => setOpen(false), 4000)
    return () => window.clearTimeout(t)
  }, [open])

  const state = !online ? 'offline' : stuck.length > 0 ? 'stuck' : pendingCount > 0 ? 'syncing' : 'online'

  // Çevrimdışı (28 Eki 2026, kullanıcı): ekrandaki son bilinen hal; ne kadar
  // eski olduğu yazıyor.
  const label =
    state === 'offline'
      ? [
          'Çevrimdışı',
          since ? `son güncelleme ${since}` : null,
          pendingCount > 0 ? `${pendingCount} sipariş cihazda` : null,
        ]
          .filter(Boolean)
          .join(', ')
      : state === 'stuck'
        ? `${stuck.length} kayıt gönderilemedi`
        : state === 'syncing'
          ? `${pendingCount} sipariş kaydediliyor…`
          : 'Tüm siparişler kayıtlı'

  const explain =
    state === 'offline'
      ? `İnternet yok. Ekranda ${since ? since + "'deki" : 'son bilinen'} menü ve masalar var. Siparişler bu cihazda duruyor, internet gelince kendiliğinden kaydedilecek. Satışa devam edebilirsiniz.`
      : state === 'stuck'
        ? 'Bazı kayıtlar sunucuya gidemedi. Görmek için dokunun.'
        : state === 'syncing'
          ? 'Siparişler kaydediliyor. Bu sayı sıfırlanınca hepsi güvende demektir.'
        : 'Bütün siparişler kaydedildi. Bekleyen yok.'

  return (
    <>
    <button
      type="button"
      className={`sync sync--${state} ${open ? 'is-open' : ''}`}
      title={explain}
      onClick={() => (state === 'stuck' ? setShowStuck(true) : setOpen((v) => !v))}
      aria-label={label}
    >
      {/* Drawn by CSS rather than typed as a character: at this size a bullet
          sits high in its line box and cannot be centred against the text. */}
      <b />
      <span className="sync__text">{label}</span>
      {/* Telefonda yazı yok, yalnız nokta; çevrimdışıyken kısası görünüyor. */}
      {state === 'offline' && (
        <span className="sync__short">
          Çevrimdışı{since && <span className="sync__time">, {since}</span>}
        </span>
      )}
      {/* Phone only: the sentence the dot stands in for. */}
      {open && <span className="sync__tip">{explain}</span>}
    </button>
    {showStuck && <StuckDialog rows={stuck} onClose={() => setShowStuck(false)} />}
    </>
  )
}
